import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, X, LayoutDashboard, FileText, UtensilsCrossed, Package, Image as ImageIcon, Star, HelpCircle, Megaphone, MessageSquare, Mail, CreditCard, BarChart3, Settings, Send, Upload, DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MENU_CATEGORIES } from "@/data/site";

const MENU_CATS = MENU_CATEGORIES;
const GAL_CATS = ["Weddings", "Corporate Events", "Private Chef", "Appetizers", "Main Courses", "Desserts"];

const SCHEMAS = {
  menu: { title: "Menu Item", endpoint: "/admin/menu", fields: [
    { k: "name", label: "Name", type: "text", required: true },
    { k: "category", label: "Category", type: "select", options: MENU_CATS },
    { k: "description", label: "Description", type: "textarea" },
    { k: "price", label: "Price per person ($)", type: "number" },
    { k: "active", label: "Active (visible on site)", type: "checkbox", default: true },
  ]},
  package: { title: "Package", endpoint: "/admin/packages", fields: [
    { k: "name", label: "Name", type: "text", required: true },
    { k: "description", label: "Description", type: "textarea" },
    { k: "menu", label: "What's included (one item per line)", type: "list" },
    { k: "price_per_person", label: "Price per person ($)", type: "number" },
    { k: "min_guests", label: "Minimum guests", type: "number", default: 10 },
    { k: "featured", label: "Featured (Most Popular)", type: "checkbox" },
    { k: "order", label: "Display order", type: "number" },
  ]},
  gallery: { title: "Gallery Image", endpoint: "/admin/gallery", fields: [
    { k: "url", label: "Image URL", type: "text", required: true },
    { k: "category", label: "Category", type: "select", options: GAL_CATS },
    { k: "title", label: "Title", type: "text" },
  ]},
  testimonial: { title: "Testimonial", endpoint: "/admin/testimonials", fields: [
    { k: "name", label: "Customer Name", type: "text", required: true },
    { k: "role", label: "Role / Location", type: "text" },
    { k: "rating", label: "Rating (1-5)", type: "number", default: 5 },
    { k: "text", label: "Review", type: "textarea", required: true },
    { k: "photo", label: "Photo URL", type: "text" },
    { k: "published", label: "Published", type: "checkbox", default: true },
  ]},
  faq: { title: "FAQ", endpoint: "/admin/faqs", fields: [
    { k: "question", label: "Question", type: "text", required: true },
    { k: "answer", label: "Answer", type: "textarea", required: true },
    { k: "category", label: "Category", type: "text", default: "General" },
    { k: "order", label: "Display order", type: "number" },
  ]},
  announcement: { title: "Promotion / Announcement", endpoint: "/admin/announcements", fields: [
    { k: "title", label: "Title", type: "text", required: true },
    { k: "body", label: "Details", type: "textarea" },
  ]},
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "analytics", label: "Website Analytics", icon: BarChart3 },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "menu", label: "Menu", icon: UtensilsCrossed },
  { id: "packages", label: "Packages", icon: Package },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "announcements", label: "Promotions", icon: Megaphone },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "newsletter", label: "Subscribers", icon: Mail },
  { id: "reports", label: "Report History", icon: Send },
  { id: "settings", label: "Settings", icon: Settings },
];

const GAL_UPLOAD_CATS = GAL_CATS;

function GalleryUpload({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [category, setCategory] = useState("Weddings");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Please choose a JPG, PNG or WEBP image."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)."); return; }
    setFile(f); setPreview(URL.createObjectURL(f));
  };
  const upload = async () => {
    if (!file) { toast.error("Choose a photo first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", category); fd.append("title", title);
      await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to upload image. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="gallery-upload-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0b0b0b] border border-white/15 max-h-[90vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-xl font-light text-white">Upload Photo</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="upload-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <label className="block border-2 border-dashed border-white/20 hover:border-gold/60 transition-colors cursor-pointer p-8 text-center" data-testid="upload-dropzone">
            <input type="file" accept="image/jpeg,image/png,image/webp" capture={false} onChange={pick} className="hidden" data-testid="upload-input" />
            {preview ? <img src={preview} alt="preview" className="max-h-48 mx-auto object-contain" data-testid="upload-preview" /> : (
              <div className="text-white/50"><Upload className="w-8 h-8 mx-auto mb-3 text-gold" /><p className="text-sm">Tap to choose a photo from your device</p><p className="text-xs mt-1">JPG, PNG or WEBP · up to 10MB</p></div>
            )}
          </label>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="lux-input" data-testid="upload-category">
              {GAL_UPLOAD_CATS.map((c) => <option key={c} value={c} className="bg-[#111]">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Title (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="lux-input" data-testid="upload-title" />
          </div>
        </div>
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button onClick={onClose} className="btn-outline flex-1 !py-3">Cancel</button>
          <button onClick={upload} disabled={busy} className="btn-gold flex-1 !py-3" data-testid="upload-save">{busy ? "Uploading…" : "Upload & Add"}</button>
        </div>
      </div>
    </div>
  );
}

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try { await login(email, password); toast.success("Welcome back"); }
    catch (e2) { const d = e2.response?.data?.detail; setErr(typeof d === "string" ? d : "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md border border-white/10 p-10 bg-[#0a0a0a]">
        <img src="/logo-official.png" alt="logo" className="h-20 mx-auto mb-6" />
        <h1 className="text-2xl font-light text-white text-center mb-8">Owner Dashboard</h1>
        <div className="space-y-6">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="lux-input" data-testid="login-email" />
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="lux-input" data-testid="login-password" />
          {err && <p className="text-red-400 text-sm" data-testid="login-error">{err}</p>}
          <button disabled={loading} className="btn-gold w-full" data-testid="login-submit">{loading ? "Signing in…" : "Sign In"}</button>
        </div>
      </form>
    </div>
  );
}

function EntityForm({ schemaKey, initial, onClose, onSaved }) {
  const schema = SCHEMAS[schemaKey];
  const build = () => {
    const s = {};
    schema.fields.forEach((f) => {
      let v = initial ? initial[f.k] : undefined;
      if (v === undefined || v === null) v = f.default !== undefined ? f.default : (f.type === "checkbox" ? false : "");
      if (f.type === "list") v = Array.isArray(v) ? v.join("\n") : (v || "");
      s[f.k] = v;
    });
    return s;
  };
  const [form, setForm] = useState(build());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {};
      schema.fields.forEach((f) => {
        let v = form[f.k];
        if (f.type === "number") v = v === "" ? 0 : Number(v);
        else if (f.type === "list") v = String(v).split("\n").map((x) => x.trim()).filter(Boolean);
        else if (f.type === "checkbox") v = !!v;
        payload[f.k] = v;
      });
      if (initial && initial.id) await api.put(`${schema.endpoint}/${initial.id}`, payload);
      else await api.post(schema.endpoint, payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e2) { toast.error("Could not save. Check the fields."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="entity-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg bg-[#0b0b0b] border border-white/15 max-h-[90vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0b0b0b]">
          <h3 className="text-xl font-light text-white">{initial && initial.id ? "Edit" : "Add"} {schema.title}</h3>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {schema.fields.map((f) => (
            <div key={f.k}>
              <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">{f.label}</label>
              {f.type === "textarea" || f.type === "list" ? (
                <textarea value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} rows={f.type === "list" ? 5 : 3} required={f.required} className="lux-input resize-none" data-testid={`field-${f.k}`} />
              ) : f.type === "select" ? (
                <select value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} className="lux-input" data-testid={`field-${f.k}`}>
                  <option value="" className="bg-[#111]">Select…</option>
                  {f.options.map((o) => <option key={o} value={o} className="bg-[#111]">{o}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <button type="button" onClick={() => set(f.k, !form[f.k])} className={`px-4 py-2 text-xs uppercase tracking-widest border ${form[f.k] ? "border-gold text-gold bg-gold/10" : "border-white/20 text-white/50"}`} data-testid={`field-${f.k}`}>{form[f.k] ? "Yes" : "No"}</button>
              ) : (
                <input type={f.type} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} required={f.required} className="lux-input" data-testid={`field-${f.k}`} />
              )}
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button type="button" onClick={onClose} className="btn-outline flex-1 !py-3">Cancel</button>
          <button disabled={saving} className="btn-gold flex-1 !py-3" data-testid="modal-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const Card = ({ children, testid }) => <div className="border border-white/10 p-5 bg-[#0a0a0a]" data-testid={testid}>{children}</div>;

function PricingPreview({ pricing }) {
  const guests = 100;
  const food = 850;
  const service = (Number(pricing.service_fee_default) || 0) + (Number(pricing.service_fee_per_guest) || 0) * guests;
  const add = 0;
  let taxable = 0;
  if (pricing.tax_food) taxable += food;
  if (pricing.tax_service) taxable += service;
  if (pricing.tax_additional) taxable += add;
  const subtotal = food + service + add;
  const tax = taxable * (Number(pricing.sales_tax_rate) || 0) / 100;
  const total = subtotal + tax;
  const deposit = total * (Number(pricing.deposit_percent) || 0) / 100;
  return (
    <div className="border border-white/10 p-5 space-y-2 text-sm bg-[#0a0a0a]" data-testid="pricing-preview">
      <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Vista previa (ejemplo: comida $850, 100 invitados)</p>
      <div className="flex justify-between"><span className="text-white/60">Comida y Menú</span><span className="text-white">${food.toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-white/60">Cargo por Servicio {pricing.service_fee_per_guest ? `($${pricing.service_fee_default} + $${pricing.service_fee_per_guest}×${guests})` : ""}</span><span className="text-white">${service.toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-white/60">Monto sujeto a impuesto</span><span className="text-white" data-testid="preview-taxable">${taxable.toFixed(2)}</span></div>
      <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-white/60">Subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-white/60">Sales Tax ({pricing.sales_tax_rate || 0}%)</span><span className="text-white">${tax.toFixed(2)}</span></div>
      <div className="flex justify-between border-t border-gold/30 pt-2"><span className="text-white">Total</span><span className="text-gold font-serif text-xl" data-testid="preview-total">${total.toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-white/60">Depósito sugerido ({pricing.deposit_percent || 0}%)</span><span className="text-gold" data-testid="preview-deposit">${deposit.toFixed(2)}</span></div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [stats, setStats] = useState({});
  const [modal, setModal] = useState(null); // { schemaKey, initial }
  const [showUpload, setShowUpload] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("7d");
  const [settings, setSettings] = useState(null);
  const [reports, setReports] = useState([]);
  const [pricing, setPricing] = useState(null);

  const load = async () => {
    const [menu, packages, gallery, tests, faqs, ann, quotes, msgs, subs, pays, st] = await Promise.all([
      api.get("/menu"), api.get("/packages"), api.get("/gallery"), api.get("/admin/testimonials"),
      api.get("/faqs"), api.get("/announcements"), api.get("/admin/quotes"), api.get("/admin/messages"),
      api.get("/admin/newsletter"), api.get("/admin/payments"), api.get("/admin/stats"),
    ].map((p) => p.catch(() => ({ data: [] }))));
    setData({ menu: menu.data, packages: packages.data, gallery: gallery.data, testimonials: tests.data, faqs: faqs.data, announcements: ann.data, quotes: quotes.data, messages: msgs.data, newsletter: subs.data, payments: pays.data });
    setStats(st.data || {});
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get(`/admin/analytics?range=${range}`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [user, range, tab]);
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, [user, tab]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  if (!user || user.role !== "admin") return <Login />;

  const del = async (url) => { if (!window.confirm("Delete this item?")) return; await api.delete(url); toast.success("Deleted"); load(); };
  const openAdd = (schemaKey) => setModal({ schemaKey, initial: null });
  const openEdit = (schemaKey, item) => setModal({ schemaKey, initial: item });
  const setQuoteStatus = async (q, status) => { await api.put(`/admin/quotes/${q.id}/status?status=${status}`); load(); };
  const setPaymentStatus = async (p, status) => { await api.put(`/admin/payments/${p.id}/status?status=${status}`); load(); };
  const runReportNow = async () => {
    try { await api.post("/admin/reports/run"); toast.success("Report is being generated and emailed"); setTimeout(() => api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {}), 2500); }
    catch { toast.error("Could not send report"); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/settings", { recipient: settings.recipient, enabled: settings.enabled, day_of_week: "sunday" }); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings"); }
  };
  const savePricing = async () => {
    try { await api.put("/admin/pricing", { service_fee_default: Number(pricing.service_fee_default), service_fee_per_guest: Number(pricing.service_fee_per_guest) || 0, sales_tax_rate: Number(pricing.sales_tax_rate), deposit_percent: Number(pricing.deposit_percent) || 0, tax_food: !!pricing.tax_food, tax_service: !!pricing.tax_service, tax_additional: !!pricing.tax_additional }); toast.success("Precios guardados"); }
    catch { toast.error("No se pudieron guardar los precios"); }
  };
  const saveQuoteFee = async (q, val) => {
    const fee = Number(val);
    if (isNaN(fee) || fee === (q.service_fee ?? null)) return;
    try { await api.put(`/admin/quotes/${q.id}/pricing`, { service_fee: fee }); toast.success("Cotización recalculada"); load(); }
    catch { toast.error("No se pudo actualizar la cotización"); }
  };

  const AddBtn = ({ schemaKey, label }) => <button onClick={() => openAdd(schemaKey)} className="btn-gold !py-2 !px-5 text-[11px]" data-testid={`add-${schemaKey}`}><Plus className="w-4 h-4" />{label}</button>;
  const RowActions = ({ schemaKey, item, delUrl }) => (
    <div className="flex gap-3 shrink-0">
      <button onClick={() => openEdit(schemaKey, item)} className="text-gold/80 hover:text-gold" data-testid={`edit-${schemaKey}-${item.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => del(delUrl)} className="text-white/40 hover:text-red-400" data-testid={`del-${schemaKey}-${item.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard">
      {modal && <EntityForm schemaKey={modal.schemaKey} initial={modal.initial} onClose={() => setModal(null)} onSaved={load} />}
      {showUpload && <GalleryUpload onClose={() => setShowUpload(false)} onSaved={load} />}
      <aside className="w-60 border-r border-white/10 bg-[#080808] p-5 flex flex-col shrink-0 sticky top-0 h-screen overflow-y-auto no-scrollbar">
        <img src="/logo-official.png" alt="logo" className="h-16 mx-auto mb-8" />
        <nav className="flex-1 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${tab === t.id ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/55 hover:text-white"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-sm text-white/55 hover:text-red-400" data-testid="admin-logout"><LogOut className="w-4 h-4" />Logout</button>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        <h1 className="text-3xl font-light text-white mb-8 capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>

        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[["Total Quotes", stats.quotes], ["New Quotes", stats.new_quotes], ["Payments", stats.payments], ["Customers", stats.customers], ["Messages", stats.messages], ["Subscribers", stats.subscribers], ["Menu Items", stats.menu_items], ["Packages", stats.packages]].map(([l, v]) => (
              <div key={l} className="bg-[#0a0a0a] p-6" data-testid={`stat-${l.replace(/\s+/g, "-")}`}><p className="text-3xl font-serif text-gold">{v ?? 0}</p><p className="text-xs text-white/50 uppercase tracking-widest mt-2">{l}</p></div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="space-y-4">
            {(data.quotes || []).map((q) => (
              <Card key={q.id} testid={`quote-row-${q.id}`}>
                <div className="flex flex-wrap justify-between gap-4">
                  <div>
                    <p className="text-white">{q.full_name} <span className="text-white/40 text-sm">· {q.event_type} · {q.guests} guests</span></p>
                    <p className="text-white/50 text-sm mt-1">{q.email} · {q.phone} · {q.city} · {q.event_date} {q.event_time}</p>
                    <p className="text-white/50 text-sm">Package: {q.selected_package || "Custom"} · Budget: {q.budget || "—"} · Est: <span className="text-gold">${q.estimated_total}</span></p>
                    {q.special_requests && <p className="text-white/40 text-sm mt-1 italic">"{q.special_requests}"</p>}
                  </div>
                  <div className="flex items-start gap-2">
                    <select value={q.status} onChange={(e) => setQuoteStatus(q, e.target.value)} className="bg-transparent border border-white/15 text-sm text-white px-2 py-1" data-testid={`quote-status-${q.id}`}>
                      {["new", "contacted", "booked", "closed"].map((s) => <option key={s} value={s} className="bg-[#111]">{s}</option>)}
                    </select>
                    <button onClick={() => del(`/admin/quotes/${q.id}`)} className="text-white/40 hover:text-red-400" data-testid={`quote-del-${q.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`quote-pricing-${q.id}`}>
                  <span className="text-white/40">Comida: <span className="text-white">${(q.food_subtotal || 0).toFixed(2)}</span></span>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">Servicio $</span>
                    <input type="number" defaultValue={q.service_fee ?? ""} onBlur={(e) => saveQuoteFee(q, e.target.value)} className="w-24 bg-transparent border border-white/15 px-2 py-1 text-white text-sm" data-testid={`quote-fee-${q.id}`} />
                  </div>
                  <span className="text-white/40">Tax: <span className="text-white">${(q.sales_tax || 0).toFixed(2)}</span></span>
                  <span className="text-white/40">Total: <span className="text-gold">${(q.estimated_total || 0).toFixed(2)}</span></span>
                </div>
              </Card>
            ))}
            {!data.quotes?.length && <p className="text-white/40">No quote requests yet.</p>}
          </div>
        )}

        {tab === "payments" && (
          <div className="space-y-4">
            {(data.payments || []).map((p) => (
              <Card key={p.id} testid={`payment-row-${p.id}`}>
                <div className="flex flex-wrap justify-between gap-4 items-center">
                  <div>
                    <p className="text-white">{p.customer_name} <span className="text-gold">${p.amount?.toFixed(2)}</span> <span className="text-white/40 text-sm">· {p.plan === "50" ? "50%" : p.plan === "100" ? "Full" : p.plan}</span></p>
                    <p className="text-white/50 text-sm mt-1">{p.customer_email} · {p.event_type} {p.event_date} · Est ${p.estimated_total?.toFixed(2)} · {new Date(p.created_at).toLocaleString()}</p>
                  </div>
                  <select value={p.status} onChange={(e) => setPaymentStatus(p, e.target.value)} className="bg-transparent border border-white/15 text-sm text-white px-2 py-1" data-testid={`payment-status-${p.id}`}>
                    {["initiated", "paid", "refunded", "cancelled"].map((s) => <option key={s} value={s} className="bg-[#111]">{s}</option>)}
                  </select>
                </div>
              </Card>
            ))}
            {!data.payments?.length && <p className="text-white/40">No payments yet.</p>}
          </div>
        )}

        {tab === "menu" && (
          <div>
            <div className="mb-6"><AddBtn schemaKey="menu" label="Add Item" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data.menu || []).map((m) => (
                <Card key={m.id} testid={`menu-row-${m.id}`}>
                  <div className="flex justify-between items-center gap-3">
                    <div><p className="text-white">{m.name} <span className="text-gold text-sm">${m.price}</span> {m.active === false && <span className="text-white/30 text-xs">(hidden)</span>}</p><p className="text-white/40 text-xs">{m.category} · {m.description}</p></div>
                    <RowActions schemaKey="menu" item={m} delUrl={`/admin/menu/${m.id}`} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "packages" && (
          <div>
            <div className="mb-6"><AddBtn schemaKey="package" label="Add Package" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data.packages || []).map((p) => (
                <Card key={p.id} testid={`pkg-row-${p.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div><p className="text-white">{p.name} <span className="text-gold text-sm">${p.price_per_person}/pp</span> {p.featured && <span className="text-gold text-xs">★</span>}</p><p className="text-white/40 text-xs">Min {p.min_guests} guests · {p.menu?.length || 0} items</p></div>
                    <RowActions schemaKey="package" item={p} delUrl={`/admin/packages/${p.id}`} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <div className="mb-6"><button onClick={() => setShowUpload(true)} className="btn-gold !py-2 !px-5 text-[11px]" data-testid="upload-photo-btn"><Upload className="w-4 h-4" />Upload Photo</button></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(data.gallery || []).map((g) => (
                <div key={g.id} className="relative group" data-testid={`gallery-row-${g.id}`}>
                  <img src={g.url} alt={g.title} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-2 transition-opacity">
                    <p className="text-gold text-xs">{g.category}</p>
                    <RowActions schemaKey="gallery" item={g} delUrl={`/admin/gallery/${g.id}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "testimonials" && (
          <div>
            <div className="mb-6"><AddBtn schemaKey="testimonial" label="Add Testimonial" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data.testimonials || []).map((t) => (
                <Card key={t.id} testid={`test-row-${t.id}`}>
                  <div className="flex justify-between gap-4">
                    <div><p className="text-white text-sm">{t.name} · {"★".repeat(t.rating)} {!t.published && <span className="text-white/30 text-xs">(hidden)</span>}</p><p className="text-white/50 text-sm mt-1 italic">"{t.text}"</p></div>
                    <RowActions schemaKey="testimonial" item={t} delUrl={`/admin/testimonials/${t.id}`} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "faqs" && (
          <div>
            <div className="mb-6"><AddBtn schemaKey="faq" label="Add FAQ" /></div>
            <div className="space-y-3">
              {(data.faqs || []).map((f) => (
                <Card key={f.id} testid={`faq-row-${f.id}`}>
                  <div className="flex justify-between gap-4"><div><p className="text-white text-sm">{f.question}</p><p className="text-white/50 text-sm mt-1">{f.answer}</p></div><RowActions schemaKey="faq" item={f} delUrl={`/admin/faqs/${f.id}`} /></div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "announcements" && (
          <div>
            <div className="mb-6"><AddBtn schemaKey="announcement" label="New Promotion" /></div>
            <div className="space-y-3">
              {(data.announcements || []).map((a) => (
                <Card key={a.id} testid={`ann-row-${a.id}`}>
                  <div className="flex justify-between gap-4"><div><p className="text-white">{a.title}</p><p className="text-white/50 text-sm mt-1">{a.body}</p></div><RowActions schemaKey="announcement" item={a} delUrl={`/admin/announcements/${a.id}`} /></div>
                </Card>
              ))}
              {!data.announcements?.length && <p className="text-white/40">No active promotions.</p>}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {(data.messages || []).map((m) => (
              <Card key={m.id} testid={`msg-row-${m.id}`}>
                <p className="text-white text-sm">{m.name} <span className="text-white/40">· {m.email} {m.phone && `· ${m.phone}`} · {m.type}</span></p>
                <p className="text-white/60 text-sm mt-1">{m.message}</p>
              </Card>
            ))}
            {!data.messages?.length && <p className="text-white/40">No messages yet.</p>}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(data.newsletter || []).map((s, i) => <Card key={i} testid={`sub-row-${i}`}><p className="text-white/70 text-sm">{s.email}</p></Card>)}
            {!data.newsletter?.length && <p className="text-white/40">No subscribers yet.</p>}
          </div>
        )}

        {tab === "analytics" && (
          <div className="space-y-8" data-testid="analytics-panel">
            <div className="flex gap-2 flex-wrap">
              {[["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["month", "This Month"]].map(([v, l]) => (
                <button key={v} onClick={() => setRange(v)} className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${range === v ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/50 hover:border-gold/50"}`} data-testid={`range-${v}`}>{l}</button>
              ))}
            </div>
            {!analytics ? <p className="text-white/40">Loading analytics…</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                  {[["Total Visits", analytics.selected.visits, analytics.change_visits_pct], ["Unique Visitors", analytics.selected.unique, null], ["New Visitors", analytics.selected.new, analytics.change_new_pct], ["Returning", analytics.selected.returning, null]].map(([l, v, pct]) => (
                    <div key={l} className="bg-[#0a0a0a] p-6" data-testid={`an-${l.replace(/\s+/g, "-")}`}>
                      <p className="text-3xl font-serif text-gold">{v ?? 0}</p>
                      <p className="text-xs text-white/50 uppercase tracking-widest mt-2">{l}</p>
                      {pct !== null && pct !== undefined && <p className={`text-xs mt-1 ${pct >= 0 ? "text-green-400" : "text-red-400"}`}>{pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}% vs previous</p>}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[["Today", analytics.today], ["This Week", analytics.week], ["This Month", analytics.month]].map(([l, d]) => (
                    <Card key={l} testid={`an-period-${l.replace(/\s+/g, "-")}`}><p className="text-xs uppercase tracking-widest text-white/40">{l}</p><p className="text-2xl font-serif text-gold mt-2">{d.visits}</p><p className="text-white/40 text-xs">visits · {d.unique} unique · {d.new} new</p></Card>
                  ))}
                </div>
                <Card testid="an-chart">
                  <p className="text-sm text-white/60 mb-4">Daily Visits</p>
                  <div className="flex items-end gap-1 h-40">
                    {analytics.selected.series.map((s) => {
                      const max = Math.max(1, ...analytics.selected.series.map((x) => x.visits));
                      return <div key={s.date} className="flex-1 flex flex-col justify-end items-center" title={`${s.date}: ${s.visits} visits`}>
                        <div className="w-full bg-gold/70 hover:bg-gold transition-colors min-h-[2px]" style={{ height: `${(s.visits / max) * 100}%` }} />
                      </div>;
                    })}
                  </div>
                  <div className="flex justify-between text-[10px] text-white/30 mt-2"><span>{analytics.selected.series[0]?.date}</span><span>{analytics.selected.series[analytics.selected.series.length - 1]?.date}</span></div>
                </Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card testid="an-pages">
                    <p className="text-sm text-white/60 mb-4">Most Visited Pages · How far visitors read</p>
                    <div className="space-y-2">
                      {analytics.selected.top_pages.map((p) => (
                        <div key={p.path} className="flex justify-between text-sm gap-4"><span className="text-white/70 truncate">{p.path}</span><span className="text-gold shrink-0">{p.visits} visits · {p.avg_scroll ?? 0}% scroll</span></div>
                      ))}
                      {!analytics.selected.top_pages.length && <p className="text-white/30 text-sm">No visits recorded yet.</p>}
                    </div>
                  </Card>
                  <Card testid="an-devices">
                    <p className="text-sm text-white/60 mb-4">Devices · Avg scroll depth {analytics.selected.avg_scroll ?? 0}%</p>
                    <div className="space-y-2">
                      {analytics.selected.devices.map((d) => (
                        <div key={d.device} className="flex justify-between text-sm capitalize"><span className="text-white/70">{d.device}</span><span className="text-gold">{d.visits}</span></div>
                      ))}
                      {!analytics.selected.devices.length && <p className="text-white/30 text-sm">No data yet.</p>}
                    </div>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-4" data-testid="reports-panel">
            <div className="mb-2"><button onClick={runReportNow} className="btn-gold !py-2 !px-5 text-[11px]" data-testid="run-report-btn"><Send className="w-4 h-4" />Send Report Now</button></div>
            {(reports || []).map((r) => (
              <Card key={r.id} testid={`report-row-${r.id}`}>
                <div className="flex flex-wrap justify-between gap-4">
                  <div>
                    <p className="text-white">{r.period_label}</p>
                    <p className="text-white/50 text-sm mt-1">{r.total} visits · {r.new} new · {r.returning} returning · avg {r.avg}/day · {r.change >= 0 ? "+" : ""}{r.change}%</p>
                    <p className="text-white/30 text-xs mt-1">Sent to {r.recipient} · {new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 h-fit uppercase tracking-widest ${r.status === "sent" ? "text-green-400 border border-green-400/30" : "text-red-400 border border-red-400/30"}`}>{r.status}</span>
                </div>
              </Card>
            ))}
            {!reports?.length && <p className="text-white/40">No reports yet. Reports are emailed automatically every Sunday at 12 PM.</p>}
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-lg space-y-6" data-testid="settings-panel">
            <p className="text-white/50 text-sm">Your automatic weekly website report is emailed every <span className="text-gold">Sunday at 12 PM</span>. It shows how many people visited and how far they read each page.</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Report recipient email</label>
              <input value={settings.recipient || ""} onChange={(e) => setSettings({ ...settings, recipient: e.target.value })} className="lux-input" data-testid="settings-recipient" />
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setSettings({ ...settings, enabled: !settings.enabled })} className={`px-4 py-2 text-xs uppercase tracking-widest border ${settings.enabled ? "border-gold text-gold bg-gold/10" : "border-white/20 text-white/50"}`} data-testid="settings-enabled">{settings.enabled ? "Enabled" : "Disabled"}</button>
              <span className="text-white/50 text-sm">Weekly report emails</span>
            </div>
            <button onClick={saveSettings} className="btn-gold !py-3" data-testid="settings-save">Save Settings</button>
          </div>
        )}

        {tab === "pricing" && pricing && (
          <div className="max-w-2xl space-y-6" data-testid="pricing-panel">
            <p className="text-white/50 text-sm">Configura el <span className="text-gold">Cargo por Servicio de Catering</span> y el <span className="text-gold">Sales Tax</span>. Los precios de los platos del menú NO cambian.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Cargo por Servicio (fijo) $</label>
                <input type="number" value={pricing.service_fee_default} onChange={(e) => setPricing({ ...pricing, service_fee_default: e.target.value })} className="lux-input" data-testid="pricing-service-fee" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Servicio por Invitado $ (opcional)</label>
                <input type="number" value={pricing.service_fee_per_guest ?? 0} onChange={(e) => setPricing({ ...pricing, service_fee_per_guest: e.target.value })} className="lux-input" data-testid="pricing-service-per-guest" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Sales Tax (%)</label>
                <input type="number" step="0.001" value={pricing.sales_tax_rate} onChange={(e) => setPricing({ ...pricing, sales_tax_rate: e.target.value })} className="lux-input" data-testid="pricing-tax-rate" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">Depósito para reservar (%)</label>
                <input type="number" step="0.1" value={pricing.deposit_percent ?? 30} onChange={(e) => setPricing({ ...pricing, deposit_percent: e.target.value })} className="lux-input" data-testid="pricing-deposit-percent" />
              </div>
            </div>
            <p className="text-white/40 text-xs">El Cargo por Servicio total = fijo + (por invitado × número de invitados). El depósito se calcula como % del total (con impuesto incluido).</p>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/40 mb-3">¿Qué componentes pagan impuesto?</p>
              <div className="flex flex-wrap gap-3">
                {[["tax_food", "Comida y Menú"], ["tax_service", "Cargo por Servicio"], ["tax_additional", "Servicios Adicionales"]].map(([k, l]) => (
                  <button type="button" key={k} onClick={() => setPricing({ ...pricing, [k]: !pricing[k] })} className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${pricing[k] ? "border-gold text-gold bg-gold/10" : "border-white/20 text-white/50"}`} data-testid={`pricing-${k}`}>{l}: {pricing[k] ? "Sí" : "No"}</button>
                ))}
              </div>
            </div>
            <PricingPreview pricing={pricing} />
            <button onClick={savePricing} className="btn-gold !py-3" data-testid="pricing-save">Guardar Precios</button>
          </div>
        )}
      </main>
    </div>
  );
}
